import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import { InputSwitch } from 'primereact/inputswitch';
import apiClient from '../../../../services/api-client';
import StorageHelper from '../../../../utils/storage-helper';
import { toMarkdown } from '../../../../utils/terms';
import AppFormInput from '../../../../components/ui/AppFormInput';
import AppFormSelect from '../../../../components/ui/AppFormSelect';
import AppButton from '../../../../components/ui/AppButton';
import AppSkeleton from '../../../../components/ui/AppSkeleton';
import GroupImageField from './GroupImageField';
import DualListbox from '../../../../components/ui/DualListbox';

const CATEGORIES = ['Legal aid', 'Group life assurance', 'Cash plan', 'Funeral policy'];

const uniqueById = (products) => [...new Map(products.map((p) => [p.ProductID, p])).values()];

const authHeaders = () => ({
    Authorization: 'Bearer ' + StorageHelper.get('token'),
    'Content-Type': 'application/json'
});

const ProductDialog = ({ visible, product, onHide, onSaved }) => {
    const isEdit = !!product;
    const toast = useRef(null);

    const [subGroups, setSubGroups] = useState([]);
    const [subGroup, setSubGroup] = useState(null);
    const [nameQuery, setNameQuery] = useState('');
    const [nameSuggestions, setNameSuggestions] = useState([]);
    const [groupImage, setGroupImage] = useState('');
    const [category, setCategory] = useState('');
    const [termsAndConditions, setTermsAndConditions] = useState('');
    const [activeStatus, setActiveStatus] = useState(true);
    const [allProducts, setAllProducts] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadProductsFor = async (subGroupId) => {
        setLoading(true);
        try {
            const response = await apiClient.get('policy/products/group/easipol/' + subGroupId, { headers: authHeaders() });
            setAllProducts(uniqueById(response.data.responseBody || []));
        } catch (e) {
          console.error(e);
            setAllProducts([]);
            toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Could not load products for this group' });
        } finally {
            setLoading(false);
        }
    };

    // Reset the form each time the dialog opens, for either a new or an existing product.
    useEffect(() => {
        if (!visible) return;
        setGroupImage(product?.groupImageBannerUrl || '');
        setCategory(product?.category || '');
        // Older records hold HTML; edit them as Markdown.
        setTermsAndConditions(toMarkdown(product?.termAndConditions));
        setActiveStatus(product ? !!product.activeStatus : true);
        setAssigned(uniqueById(product?.planProducts || []));
        setAllProducts([]);

        if (product) {
            setSubGroup(product.easipolPolicySubGroup || null);
            loadProductsFor(product.easipolSubGroupId);
        } else {
            setSubGroup(null);
            setNameQuery('');
            apiClient.get('policy/products/group', { headers: authHeaders() })
                .then((response) => setSubGroups(response.data.responseBody || []))
                .catch(() => setSubGroups([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, product]);

    const handleSubGroupChange = (selected) => {
        setSubGroup(selected);
        setNameQuery(selected || '');
        setAssigned([]);
        setAllProducts([]);
        if (selected) loadProductsFor(selected.SubGroupID);
    };

    const subGroupId = isEdit ? product.easipolSubGroupId : subGroup?.SubGroupID;

    const handleSave = async () => {
        if (!subGroupId) {
            toast.current.show({ severity: 'warn', summary: 'Missing name', detail: 'Select the product group first' });
            return;
        }
        if (assigned.length === 0) {
            toast.current.show({ severity: 'warn', summary: 'No products', detail: 'Assign at least one product' });
            return;
        }

        const payload = {
            accessToken: '',
            operation: 'SaveActivePlansProducts',
            requestBody: {
                easipolSubGroupId: subGroupId,
                activeStatus,
                category,
                groupImageBannerUrl: groupImage,
                termAndConditions: termsAndConditions,
                // Same order as the Assigned list; existing extensions are carried over unchanged.
                productsList: uniqueById(assigned).map((p) => ({
                    productID: p.ProductID,
                    productExtensions: [
                        ...(p.productExtensions || []).map((ext) => ext.ProductID),
                        ...(p.includeParentProductInExtensions ? [p.ProductID] : [])
                    ]
                }))
            }
        };

        setSaving(true);
        try {
            const response = await apiClient.post('policy/products/active', payload, { headers: authHeaders() });
            if (response.data.status === 'SUCCESS') {
                toast.current.show({ severity: 'success', summary: 'SUCCESS', detail: response.data.message });
                onSaved?.();
            } else {
                toast.current.show({ severity: 'error', sticky: true, summary: 'Failed', detail: response.data.message });
            }
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', sticky: true, summary: 'Failed', detail: 'Could not save the product' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                header={isEdit ? 'Edit Product' : 'Add New Product'}
                visible={visible}
                maximizable
                style={{ width: '70vw' }}
                breakpoints={{ '960px': '95vw' }}
                onHide={onHide}
            >
                <div className="w-full flex flex-col gap-2 mt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    {isEdit ? (
                        <AppFormInput label="Name" value={subGroup?.Description || ''} readOnly disabled />
                    ) : (
                        <div className="form-control w-full space-y-1 mb-4">
                            <label htmlFor="subGroupName" className="label py-1"><span className="label-text text-xs font-bold text-slate-700">Name</span></label>
                            <AutoComplete
                                inputId="subGroupName"
                                value={nameQuery}
                                suggestions={nameSuggestions}
                                completeMethod={(e) => setNameSuggestions(
                                    subGroups.filter((g) => g.Description?.toLowerCase().includes(e.query.toLowerCase()))
                                )}
                                field="Description"
                                dropdown
                                dropdownMode="blank"
                                forceSelection
                                onChange={(e) => {
                                    setNameQuery(e.value);
                                    // Typing over a chosen group drops that choice (and its products) until a new one is picked.
                                    if (typeof e.value === 'string' && subGroup) {
                                        setSubGroup(null);
                                        setAssigned([]);
                                        setAllProducts([]);
                                    }
                                }}
                                onSelect={(e) => handleSubGroupChange(e.value)}
                                placeholder="Type to search product groups"
                                className="w-full"
                                inputClassName="w-full"
                            />
                        </div>
                    )}

                    <AppFormSelect
                        label="Product Category"
                        value={category}
                        options={CATEGORIES}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="Select a category"
                    />
                    </div>

                    <GroupImageField value={groupImage} onChange={setGroupImage} />

                    <div className="form-control w-full mb-4">
                        <label htmlFor="termsAndConditions" className="label py-1"><span className="label-text text-xs font-bold text-slate-700">Terms & Conditions</span></label>
                        <textarea
                            id="termsAndConditions"
                            className="textarea textarea-bordered w-full min-h-[150px]"
                            placeholder="Terms and conditions"
                            value={termsAndConditions}
                            onChange={(e) => setTermsAndConditions(e.target.value)}
                        />
                        <span className="text-xs text-slate-500 mt-1">Markdown supported: **bold**, *italic*, "- " for bullet lists, "1. " for numbered lists.</span>
                    </div>

                    <div className="form-control w-full mb-4">
                        <div className="label py-1"><span className="label-text text-xs font-bold text-slate-700">Active Products</span></div>
                        {(loading) && (<AppSkeleton height="14rem" className="rounded-lg" />)}
                        {!(loading) && (!subGroupId) && (<p className="text-sm text-slate-500">Select a product group to choose its products.</p>)}
                        {!(loading) && !(!subGroupId) && (<DualListbox
                                available={allProducts}
                                assigned={assigned}
                                onChange={setAssigned}
                                getKey={(p) => p.ProductID}
                                getLabel={(p) => p.ProductName1}
                                availableTitle="Available"
                                assignedTitle="Assigned (display order)"
                            />)}
                    </div>

                    <label className="flex items-center gap-4 p-4 border border-base-200 rounded-lg">
                        <InputSwitch checked={activeStatus} onChange={(e) => setActiveStatus(e.value)} />
                        <span className="label-text font-medium text-gray-700">{activeStatus ? 'Active' : 'Disabled'}</span>
                    </label>

                    <div className="mt-4 flex justify-end gap-2 pt-4 border-t border-base-200">
                        <AppButton variant="ghost" onClick={onHide}>Cancel</AppButton>
                        <AppButton variant="primary" icon="pi pi-check" loading={saving} onClick={handleSave}>
                            {isEdit ? 'Save Changes' : 'Create Product'}
                        </AppButton>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default ProductDialog;
